/**
 * Advisory Wizard Params
 *
 * The single whitelist gate between the URL's `?path=`/`?step=` query
 * params and typed wizard state. The params are client wizard state living
 * in a user-editable place — reload, browser back/forward and sharing all
 * survive it for free, with zero storage and zero cleanup, and it never
 * writes to the goal document.
 *
 * `parseAdvisoryPath` / `parseAdvisoryStep` are the ONLY place in the
 * codebase that turns a raw query string into a typed
 * `AdvisoryPath`/`AdvisoryWizardStep`. No other module may branch on
 * `searchParams.get(ADVISORY_PATH_PARAM)` / `searchParams.get(ADVISORY_STEP_PARAM)`'s
 * raw string, interpolate it into rendered copy, or concatenate it into an
 * i18n key path — every consumer goes through `useAdvisoryWizardParams`
 * instead.
 *
 * Locked policy: the initial fork choice is a PUSH (the default below), so
 * browser-back from a step lands on the fork screen rather than skipping
 * it; every subsequent step-to-step transition passes `{ replace: true }`
 * so the wizard's internal navigation does not pile up history entries a
 * user would have to click back through one at a time.
 *
 * `.tsx` extension: the hook's own test mounts a probe component from this
 * module family, even though this module itself renders nothing.
 */
import { useSearchParams } from 'react-router-dom';
import type { AdvisoryPath, AdvisoryWizardStep } from './advisoryStepPosition';

/** The single spelling of the path query-param name. */
export const ADVISORY_PATH_PARAM = 'path';

/** The single spelling of the step query-param name. */
export const ADVISORY_STEP_PARAM = 'step';

/**
 * Whitelist parser: returns `raw` narrowed to `AdvisoryPath` only when it is
 * an exact match for one of the two literal members, and `null` for
 * anything else — different casing, surrounding whitespace, an empty
 * string, and `null` itself all return `null`. No `as` cast, no `includes`
 * over a widened `string[]`, no regex.
 */
export function parseAdvisoryPath(raw: string | null): AdvisoryPath | null {
  if (raw === 'express' || raw === 'customize') {
    return raw;
  }
  return null;
}

/**
 * Whitelist parser: returns `raw` narrowed to `AdvisoryWizardStep` only when
 * it is an exact match for one of the two literal members (was three — the
 * deleted criteria step is gone with the union member it whitelisted), and
 * `null` for anything else — different casing, surrounding whitespace, an
 * empty string, and `null` itself all return `null`. No `as` cast, no
 * `includes` over a widened `string[]`, no regex.
 */
export function parseAdvisoryStep(raw: string | null): AdvisoryWizardStep | null {
  if (raw === 'keyQuestions' || raw === 'evidence') {
    return raw;
  }
  return null;
}

export interface AdvisoryWizardParams {
  path: AdvisoryPath | null;
  step: AdvisoryWizardStep | null;
  setWizardParams: (
    next: { path: AdvisoryPath; step: AdvisoryWizardStep },
    options?: { replace?: boolean }
  ) => void;
}

/**
 * Reads and writes the two wizard query params. Calls `useSearchParams()`
 * exactly once, runs both whitelist parsers over the raw values, and
 * exposes `setWizardParams` which writes the WHOLE search string afresh
 * (never merges) — so an unknown param a user hand-appended (or a stale
 * param from a previous step) does not survive a transition. `next` is
 * typed to the unions, so writing a non-whitelisted value is a compile
 * error, not a runtime check.
 */
export function useAdvisoryWizardParams(): AdvisoryWizardParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const path = parseAdvisoryPath(searchParams.get(ADVISORY_PATH_PARAM));
  const step = parseAdvisoryStep(searchParams.get(ADVISORY_STEP_PARAM));

  const setWizardParams = (
    next: { path: AdvisoryPath; step: AdvisoryWizardStep },
    options?: { replace?: boolean }
  ) => {
    setSearchParams(
      { [ADVISORY_PATH_PARAM]: next.path, [ADVISORY_STEP_PARAM]: next.step },
      { replace: options?.replace ?? false }
    );
  };

  return { path, step, setWizardParams };
}
